import {
  Button,
  IconButton,
  Card,
  Container,
  Dialog,
  Stack,
  Menu,
  MenuItem,
  TextField,
  Typography,
  Grid,
  Tooltip,
  Paper,
  Tabs,
  Tab,
  Divider,
  Autocomplete,
  Box,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { ChangeEvent, MouseEvent, useCallback, useEffect, useState, useMemo } from 'react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { FilterList as FilterListIcon } from '@mui/icons-material';
import moment from 'moment';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import Page from 'src/components/Page';
import SearchTextField from 'src/components/SearchTextField';
import FilterDrawer from 'src/components/FilterDrawer';
import CheckboxList from 'src/components/CheckboxList';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useActivityDefinitionService } from './api/DefinitionService';
import { getOrganizationsByIds } from 'src/careflow/ph-core/admin/organization/service/organizations';
import { ActivityDefinition } from 'src/careflow/ph-core/fhir-client/resources/resources';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { debounce } from 'src/utils/timers';
import useValueSetsByIdentifiers from 'src/hooks/useValueSetsByIdentifier';
import DatePickerMoment from 'src/components/DatePickerMoment';
import { BreadCrumbTitle, GridItem, GridSection } from 'src/components/CustomModal';
import CancelButton from 'src/careflow/common/CancelButton';
import {
  createTransactionBundle,
  downloadJson,
  downloadCsv,
  mapResourcesWithFhirPath,
} from 'src/utils/fhirExport';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';

const activityInitialState: Partial<ActivityDefinition> = {
  resourceType: 'ActivityDefinition',
  name: '',
  title: '',
  description: '',
  status: 'active',
  kind: 'Task',
  //kind: 'ServiceRequest',
  intent: 'proposal',
  priority: 'routine',
  effectivePeriod: { start: moment().toISOString(), end: undefined },
  timingDuration: { },
  dynamicValue: [],
  relatedArtifact: [],
  code: { coding: [] },
  useContext: [],
};

const EXPORT_COLUMNS = {
  ID: 'id',
  Code: 'name',
  Title: 'title',
  Kind: 'kind',
  'Activity Code': 'code.coding.code',
  Status: 'status',
  Description: 'description',
  Organization: "useContext.where(code.code='workflow').value.reference",
  'Timing Duration Value': 'timingDuration.value',
  'Timing Duration Unit': 'timingDuration.unit',
  'Last Updated': 'meta.lastUpdated',
  'Dynamic Values': 'dynamicValue',
  'Related Artifacts': 'relatedArtifact',
};

export default function ActivityDefinitionList() {
  const { getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();

  const { componentsData } = useTenantConfigData();

  const { getFiltered, create, update } = useActivityDefinitionService();

  const [activities, setActivities] = useState<ActivityDefinition[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('general');

  const [searchValue, setSearchValue] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [kindFilter, setKindFilter] = useState<string[]>([]);
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const [activityInForm, setActivityInForm] = useState<Partial<ActivityDefinition>>(activityInitialState);
  const [editingActivity, setEditingActivity] = useState<ActivityDefinition | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedActivity, setSelectedActivity] = useState<ActivityDefinition | null>(null);

  const permissions = {
    isAllowedToAdd: checkAclValidation({ acls: [crsAcls.CRS.ACTIVITY_DEFINITION.ADD] }),
    isAllowedToEdit: checkAclValidation({ acls: [crsAcls.CRS.ACTIVITY_DEFINITION.EDIT] }),
  };

  const {
    valueSets: [
      taskTypes,
      taskStatuses,
      taskIntents,
      taskPriorities,
      serviceRequestStatuses,
      activityStatusVS,
      activityIntentVS,
      activityKindVS,
      durationUnitsVS,
      taskInputTypesVS
    ],
  } = useValueSetsByIdentifiers([
    'ph-task-type-codes',
    'ph-task-status',
    'ph-task-intent',
    'ph-task-priority',
    'service-request-status',
    'ph-activity-definition-status',
    'ph-activity-definition-intent',
    'ph-activity-definition-kind',
    'ph-activity-definition-duration-units',
    'ph-task-input-types'
  ]);

  const activityStatusOptions = useMemo(() => activityStatusVS?.asList?.() || [], [activityStatusVS]);
  const activityIntentOptions = useMemo(() => activityIntentVS?.asList?.() || [], [activityIntentVS]);
  const activityKindOptions = useMemo(() => activityKindVS?.asList?.() || [], [activityKindVS]);
  const durationUnitOptions = useMemo(() => durationUnitsVS?.asList?.() || [], [durationUnitsVS]);

  const artifactExtensionOptions = useMemo(() => {
    const configUrl = componentsData?.task?.taskInputTypesExtensionUrl;
    return (taskInputTypesVS?.asList?.() || []).map((item) => ({
      code: item.code,
      display: item.display,
      system: taskInputTypesVS?.getConceptSystem?.(),
      extensionUrl: configUrl || 'https://terminology.patientholistics.app/fhir/StructureDefinition/ph-task-input-types',
    }));
  }, [componentsData, taskInputTypesVS]);

  const dynamicPathOptions = useMemo(() => {
    if (activityInForm.kind === 'ServiceRequest') {
      return [
        { path: 'serviceRequest.status', label: 'Service Request Status' },
        { path: 'serviceRequest.intent', label: 'Service Request Intent' },
        { path: 'serviceRequest.priority', label: 'Service Request Priority' },
      ];
    }

    // Default to Task options
    return [
      { path: 'task.status', label: 'Task Status' },
      { path: 'task.intent', label: 'Task Intent' },
      { path: 'task.priority', label: 'Task Priority' },
    ];
  }, [activityInForm.kind]);

  const orgIds = useMemo(
    () => (currentUser?.organizations || (currentUser?.organization_uuid ? [currentUser.organization_uuid] : [])),
    // Use stringified joined values as dependency to ensure reference stability
    [currentUser?.organizations?.join(','), currentUser?.organization_uuid]
  );

  const fetchOrganizations = useCallback(async () => {
    try {
      if (orgIds && orgIds.length > 0) {
        const response = await getOrganizationsByIds(orgIds);
        setOrganizations(response);
      }
    } catch (error) {
      console.error('Failed to fetch organizations', error);
    }
  }, [orgIds]);

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getFiltered(orgIds);
      setActivities(response);
    } catch (error) {
      console.error('Failed to fetch activity definitions', error);
    } finally {
      setLoading(false);
    }
  }, [orgIds, getFiltered]);

  useEffect(() => {
    fetchActivities();
    fetchOrganizations();
  }, [fetchActivities, fetchOrganizations]);

  const handleOpen = () => {
    setEditingActivity(null);
    setActivityInForm(activityInitialState);
    setActiveSection('general');
    setOpen(true);
  };

  const handleSearchChange = useMemo(
    () =>
      debounce((val: string) => {
        setSearch(val);
      }, 600),
    []
  );

  const filteredActivities = useMemo(() => {
    let result = [...activities];

    if (search) {
      const lowerSearch = search.toLowerCase();
      result = result.filter(
        (a) =>
          a.name?.toLowerCase().includes(lowerSearch) ||
          a.description?.toLowerCase().includes(lowerSearch)
      );
    }

    if (statusFilter.length > 0) {
      result = result.filter((a) => a.status && statusFilter.includes(a.status));
    }

    if (kindFilter.length > 0) {
      result = result.filter((a) => a.kind && kindFilter.includes(a.kind));
    }

    return result;
  }, [activities, search, statusFilter, kindFilter]);

  const handleExportMenuOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportAnchorEl(null);
  };

  const handleExportTransactionBundle = () => {
    const bundle = createTransactionBundle(filteredActivities);
    const fileName = `activity-definitions-transaction-${moment().format('YYYYMMDDHHmm')}.json`;
    downloadJson(bundle, fileName);
    handleExportMenuClose();
  };

  const handleExportCsv = () => {
    const dataToExport = mapResourcesWithFhirPath(filteredActivities, EXPORT_COLUMNS);
    const fileName = `activity-definitions-${moment().format('YYYYMMDDHHmm')}.csv`;
    downloadCsv(dataToExport, fileName);
    handleExportMenuClose();
  };

  const handleMenuOpen = useCallback((event: MouseEvent<HTMLButtonElement>, activity: ActivityDefinition) => {
    setAnchorEl(event.currentTarget);
    setSelectedActivity(activity);
  }, []);

  const columns = useMemo<GridColDef[]>(() => [
    { field: 'name', headerName: 'Name', flex: 1, renderCell: (params) => <Typography sx={{ fontWeight: 'bold' }}>{params.value}</Typography> },
      { field: 'description', headerName: 'Description', flex: 1.5 },
      {
        field: 'useContext',
        headerName: 'Organization',
        flex: 1,
        valueGetter: (params) => {
          const orgRef = params.row.useContext?.find((uc: any) => uc.valueReference?.reference?.includes('Organization/'))?.valueReference?.reference;
          if (!orgRef) return '';
          const orgId = orgRef.split('/').pop();
          return organizations.find((o) => o.fhirId === orgId)?.name || orgId;
        },
      },
      { field: 'status', headerName: 'Status', flex: 0.8 },
      { field: 'kind', headerName: 'Kind', flex: 1 },
      {
        field: 'lastUpdated',
        headerName: 'Last Updated',
        flex: 1,
        valueGetter: (params) => {
          return params.row.meta?.lastUpdated ? moment(params.row.meta.lastUpdated).format('YYYY-MM-DD HH:mm') : '';
        },
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        width: 80,
        align: 'right',
        renderCell: (params) =>
          permissions.isAllowedToEdit && (
            <IconButton onClick={(e) => handleMenuOpen(e, params.row)}>
              <Iconify icon={'eva:more-vertical-fill'} width={20} height={20} />
            </IconButton>
          ),
      },
    ],
    [handleMenuOpen, organizations, permissions.isAllowedToEdit]
  );


  const handleClose = () => {
    setOpen(false);
    setActivityInForm(activityInitialState);
    setEditingActivity(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedActivity(null);
  };

  const handleEditClick = () => {
    if (selectedActivity) {
      setEditingActivity(selectedActivity);
      setActivityInForm(selectedActivity);
      setActiveSection('general');
      setOpen(true);
    }
    handleMenuClose();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setActivityInForm((prev) => ({ ...prev, [name]: value }));
  };

  const getValuesForPath = useCallback((path: string) => {
    switch (path) {
      case 'task.status':
        return taskStatuses?.asList?.() ?? [];
      case 'serviceRequest.status':
        return serviceRequestStatuses?.asList?.() ?? [];
      case 'task.intent':
      case 'serviceRequest.intent':
        return taskIntents?.asList?.() ?? [];
      case 'task.priority':
      case 'serviceRequest.priority':
        return taskPriorities?.asList?.() ?? [];
      default: return [];
    }
  }, [taskStatuses, taskIntents, taskPriorities]);

  const handleAddDynamicValue = () => {
    const defaultPath = activityInForm.kind === 'ServiceRequest' ? 'serviceRequest.status' : 'task.status';
    setActivityInForm((prev) => ({
      ...prev,
      dynamicValue: [
        ...(prev.dynamicValue || []),
        { path: defaultPath, expression: { language: 'text/plain', expression: '', name: defaultPath, description: '' } },
      ],
    }));
  };

  const handleUpdateDynamicValue = (index: number, field: 'path' | 'value' | 'description', value: any) => {
    setActivityInForm((prev) => {
      const newList = [...(prev.dynamicValue || [])];
      if (field === 'path') {
        newList[index] = { 
          ...newList[index], 
          path: value, 
          expression: { ...newList[index].expression, name: value, expression: '' } 
        };
      } else if (field === 'value') {
        newList[index] = { 
          ...newList[index], 
          expression: { ...newList[index].expression, expression: value } 
        };
      } else if (field === 'description') {
        newList[index] = {
          ...newList[index],
          expression: { ...newList[index].expression, description: value }
        };
      }
      return { ...prev, dynamicValue: newList };
    });
  };

  const handleRemoveDynamicValue = (index: number) => {
    setActivityInForm((prev) => ({
      ...prev,
      dynamicValue: prev.dynamicValue?.filter((_, i) => i !== index),
    }));
  };


  const handleSaveActivity = async () => {
    try {
      setSaving(true);
      if (editingActivity && editingActivity.id) {
        await update(editingActivity.id, activityInForm as ActivityDefinition);
      } else {
        await create(activityInForm as ActivityDefinition);
      }
      handleClose();
      fetchActivities();
    } catch (error) {
      console.error('Failed to save activity definition', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page title="Activity Definitions">
      <Container>
        <HeaderBreadcrumbs
          heading=""
          title="Activity Definition List"
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Activity Definitions' }]}
          action={
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={handleExportMenuOpen}
                endIcon={<Iconify icon={'eva:chevron-down-fill'} />}
              >
                Export as
              </Button>
              {permissions.isAllowedToAdd && (
                <Button
                  variant="contained"
                  startIcon={<Iconify icon={'eva:plus-fill'} />}
                  onClick={handleOpen}
                >
                  New Activity
                </Button>
              )}
            </Stack>
          }
        />
        <Card sx={{ p: 2 }}>
          <Grid container spacing={2} sx={{ mb: 2, px: 1 }} justifyContent="space-between" alignItems="center">
            <Grid item xs={12} sm={4}>
              <SearchTextField
                value={searchValue}
                onChange={(event) => {
                  const value = event.target.value;
                  setSearchValue(value);
                  handleSearchChange(value);
                }}
                fullWidth
                placeholder="Search by name or description..."
              />
            </Grid>

            <Grid item xs={12} sm={4} display="flex" justifyContent="flex-end">
              <Tooltip title="Filter">
                <IconButton onClick={() => setIsFilterDrawerOpen(true)}>
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>

          <DataGrid
            autoHeight
            rows={filteredActivities}
            columns={columns}
            loading={loading}
            initialState={{
              sorting: {
                sortModel: [{ field: 'name', sort: 'asc' }],
              },
            }}
            pageSize={pageSize}
            onPageSizeChange={(newSize) => setPageSize(newSize)}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            sx={{
              '& .MuiDataGrid-columnHeader': { backgroundColor: '#f4f6f8' },
            }}
          />
        </Card>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleEditClick}>
            <Iconify icon={'eva:edit-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            Edit
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={exportAnchorEl}
          open={Boolean(exportAnchorEl)}
          onClose={handleExportMenuClose}
        >
          <MenuItem onClick={handleExportTransactionBundle}>
            <Iconify icon={'eva:file-text-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            Bundle transaction
          </MenuItem>
          <MenuItem onClick={handleExportCsv}>
            <Iconify icon={'eva:file-fill'} sx={{ mr: 2, width: 20, height: 20 }} />
            CSV
          </MenuItem>
        </Menu>
        <Dialog
          open={open}
          onClose={handleClose}
          fullWidth
          maxWidth={false}
          PaperProps={{
            sx: {
              width: '70vw',
              paddingX: 3,
              maxHeight: '90vh',
            },
          }}
        >
          <Box display={'flex'} flexDirection={'row'} alignItems={'center'} sx={{ paddingTop: 1, gap: 1 }}>
            <Typography variant="h6">{editingActivity ? 'Edit Activity Definition' : 'New Activity Definition'}</Typography>
            <BreadCrumbTitle useSeparator title="Activity Definitions" />
            <BreadCrumbTitle useSeparator title={editingActivity ? 'Edit' : 'Create'} />
          </Box>

          <Box display="flex" sx={{ mt: 3, gap: 2 }}>
            <Paper
              elevation={0}
              sx={{
                width: '250px',
                minWidth: '250px',
                backgroundColor: '#f4f6f8',
                borderRadius: 1,
                p: 2,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: '#212b36' }}>
                Setup Wizard
              </Typography>
              <Tabs
                orientation="vertical"
                value={activeSection}
                onChange={(_, v) => setActiveSection(newValue => v)}
                sx={{
                  '& .MuiTabs-indicator': { display: 'none' },
                  '& .MuiTab-root': {
                    alignItems: 'flex-start',
                    textAlign: 'left',
                    minHeight: 'auto',
                    padding: '12px 16px',
                    marginBottom: '4px',
                    borderRadius: 1,
                    textTransform: 'none',
                    '&.Mui-selected': { backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 600 },
                  },
                }}
              >
                <Tab value="general" label="General Info" />
                <Tab value="timing" label="Timing & Intent" />
                <Tab value="dynamicValues" label="Dynamic Values" />
                <Tab value="artifacts" label="Related Artifacts" />
              </Tabs>
            </Paper>

            <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 'calc(90vh - 200px)', p: 1 }}>
              <Grid container justifyContent={'space-between'}>
              {activeSection === 'general' && (
                <Grid item xs={12}> 
                  <GridSection>
                    <GridItem>
                      <TextField name="name" label="Technical Name (ID)" value={activityInForm.name} onChange={handleInputChange} fullWidth />
                    </GridItem>
                    <GridItem>
                      <TextField name="title" label="Display Title" value={activityInForm.title} onChange={handleInputChange} fullWidth />
                    </GridItem>
                  </GridSection>
                  <GridSection>
                    <GridItem xs={12}>
                      <Autocomplete
                        value={organizations.find((o) => 
                          activityInForm.useContext?.some(uc => uc.valueReference?.reference === `Organization/${o.fhirId}`)
                        ) || null}
                        options={organizations}
                        getOptionLabel={(o) => o.name || ''}
                        onChange={(_, v) => {
                          const otherContexts = activityInForm.useContext?.filter(uc => !uc.valueReference?.reference?.startsWith('Organization/')) || [];
                          const newContexts = v ? [
                            ...otherContexts,
                            {
                              code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'workflow' },
                              valueReference: { reference: `Organization/${v?.fhirId}` }
                            }
                          ] : otherContexts;
                          setActivityInForm(p => ({ ...p, useContext: newContexts }));
                        }}
                        renderInput={(p) => <TextField {...p} label="Organization Owner" />}
                      />
                    </GridItem>
                  </GridSection>
                  <GridSection>
                    <GridItem xs={12}>
                      <TextField name="description" label="Description" value={activityInForm.description} onChange={handleInputChange} fullWidth multiline rows={3} />
                    </GridItem>
                  </GridSection>
                  <GridSection>
                    <GridItem>
                      <Autocomplete
                        value={activityStatusOptions.find(o => o.code === activityInForm.status) || null}
                        options={activityStatusOptions}
                        getOptionLabel={o => o.display || ''}
                        onChange={(_, v) => setActivityInForm(p => ({ ...p, status: v?.code as any }))}
                        renderInput={p => <TextField {...p} label="Status" />}
                      />
                    </GridItem>
                    <GridItem>
                      <Autocomplete
                        value={activityKindOptions.find(o => o.code === activityInForm.kind) || null}
                        options={activityKindOptions}
                        getOptionLabel={o => o.display || ''}
                        onChange={(_, v) => setActivityInForm(p => ({ ...p, kind: v?.code as any }))}
                        renderInput={p => <TextField {...p} label="Kind" />}
                      />
                    </GridItem>
                  </GridSection>
                  <GridSection>
                    <GridItem xs={12}>
                      <Autocomplete
                        value={taskTypes?.asList?.().find(c => c.code === activityInForm.code?.coding?.[0]?.code)}
                        options={taskTypes?.asList?.() ?? []}
                        getOptionLabel={o => o.display || ''}
                        onChange={(_, v) => setActivityInForm(p => ({
                          ...p,
                          code: { coding: [{ code: v?.code, display: v?.display, system: taskTypes?.getConceptSystem?.() }] }
                        }))}
                        renderInput={p => <TextField {...p} label="Activity Code" />}
                      />
                    </GridItem>
                  </GridSection>
                </Grid>
              )}

              {activeSection === 'timing' && (
                <Grid item xs={12}>
                  <GridSection mt={0}>
                    <GridItem>
                      <Autocomplete
                        value={activityIntentOptions.find(o => o.code === activityInForm.intent) || null}
                        options={activityIntentOptions}
                        getOptionLabel={o => o.display || ''}
                        onChange={(_, v) => setActivityInForm(p => ({ ...p, intent: v?.code as any }))}
                        renderInput={p => <TextField {...p} label="Intent" />}
                      />
                    </GridItem>
                    <GridItem>
                      <Autocomplete
                        value={taskPriorities?.asList?.().find(o => o.code === activityInForm.priority)}
                        options={taskPriorities?.asList?.() ?? []}
                        getOptionLabel={o => o.display || ''}
                        onChange={(_, v) => setActivityInForm(p => ({ ...p, priority: v?.code as any }))}
                        renderInput={p => <TextField {...p} label="Priority" />}
                      />
                    </GridItem>
                  </GridSection>
                  <GridSection>
                    <GridItem>
                      <TextField
                        type="number"
                        label="Timing Value"
                        value={activityInForm.timingDuration?.value ?? ''}
                        onChange={e => setActivityInForm(p => ({ ...p, timingDuration: { ...(p.timingDuration || { unit: 'd' }), value: Number(e.target.value) } }))}
                        fullWidth
                      />
                    </GridItem>
                    <GridItem>
                      <Autocomplete
                        value={durationUnitOptions.find(o => o.code === activityInForm.timingDuration?.unit) || null}
                        options={durationUnitOptions}
                        getOptionLabel={o => o.display || ''}
                        onChange={(_, v) => setActivityInForm(p => ({ ...p, timingDuration: { ...(p.timingDuration || { value: 1 }), unit: v?.code } }))}
                        renderInput={p => <TextField {...p} label="Timing Unit" />}
                      />
                    </GridItem>
                  </GridSection>
                  <GridSection>
                    <GridItem xs={6}>
                      <DatePickerMoment
                        label="Effective Period Start"
                        value={activityInForm.effectivePeriod?.start ? moment(activityInForm.effectivePeriod.start) : null}
                        onChange={v => setActivityInForm(p => ({ ...p, effectivePeriod: { ...p.effectivePeriod, start: v?.toISOString() } }))}
                      />
                    </GridItem>
                    <GridItem xs={6}>
                      <DatePickerMoment
                        label="Effective Period End"
                        value={activityInForm.effectivePeriod?.end ? moment(activityInForm.effectivePeriod.end) : null}
                        onChange={v => setActivityInForm(p => ({ ...p, effectivePeriod: { ...p.effectivePeriod, end: v?.toISOString() } }))}
                      />
                    </GridItem>
                  </GridSection>
                </Grid>
              )}

              {activeSection === 'dynamicValues' && (
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1">Define Default Task Parameters</Typography>
                    <Button startIcon={<AddIcon />} onClick={handleAddDynamicValue} variant="outlined">Add Value</Button>
                  </Box>
                  {activityInForm.dynamicValue?.map((dv, index) => (
                    <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                      <Stack spacing={2}>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Mapping #{index + 1}</Typography>
                          <IconButton size="small" onClick={() => handleRemoveDynamicValue(index)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Autocomplete
                              value={dynamicPathOptions.find(p => p.path === dv.path)}
                              options={dynamicPathOptions}
                              getOptionLabel={o => o.label}
                              onChange={(_, v) => handleUpdateDynamicValue(index, 'path', v?.path)}
                              renderInput={p => <TextField {...p} label="Field to Set" />}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Autocomplete
                              value={getValuesForPath(dv.path).find(v => v.code === dv.expression?.expression)}
                              options={getValuesForPath(dv.path)}
                              getOptionLabel={o => o.display || ''}
                              onChange={(_, v) => handleUpdateDynamicValue(index, 'value', v?.code)}
                              renderInput={p => <TextField {...p} label="Default Value" />}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Description"
                              value={dv.expression?.description || ''}
                              onChange={e => handleUpdateDynamicValue(index, 'description', e.target.value)}
                              placeholder="Explain why this value is being set..."
                            />
                          </Grid>
                        </Grid>
                      </Stack>
                    </Paper>
                  ))}
                </Grid>
              )}

              {activeSection === 'artifacts' && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1">Related Resources & Documentation</Typography>
                  <Typography variant="caption" color="textSecondary">Manage links to external forms, guidelines, or assessment URLs.</Typography>
                  <Button variant="outlined" onClick={() => setActivityInForm(p => ({ ...p, relatedArtifact: [...(p.relatedArtifact || []), { type: 'justification', url: '', extension: [] }] }))} sx={{ mt: 1, mb: 2, display: 'block' }}>Add Artifact</Button>
                  {activityInForm.relatedArtifact?.map((art, index) => (
                    <Grid container spacing={2} key={index} alignItems="center" sx={{ mb: 2 }}>
                      <Grid item xs={5}>
                        <Autocomplete
                          value={artifactExtensionOptions.find(o => o.code === art.extension?.[0]?.valueCodeableConcept?.coding?.[0]?.code)}
                          options={artifactExtensionOptions}
                          getOptionLabel={o => o.display || ''}
                          onChange={(_, v) => {
                            const newList = [...activityInForm.relatedArtifact!];
                            if (v) {
                              newList[index] = {
                                ...newList[index],
                                extension: [{
                                  url: v.extensionUrl,
                                  valueCodeableConcept: {
                                    coding: [{ code: v.code, display: v.display, system: v.system }],
                                    text: v.display
                                  }
                                }]
                              };
                            } else {
                              newList[index] = { ...newList[index], extension: [] };
                            }
                            setActivityInForm(p => ({ ...p, relatedArtifact: newList }));
                          }}
                          renderInput={p => <TextField {...p} label="Input Type" />}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <TextField 
                          fullWidth 
                          label="Resource URL" 
                          value={art.url || ''} 
                          onChange={e => {
                            const newList = [...activityInForm.relatedArtifact!];
                            newList[index] = { ...newList[index], url: e.target.value };
                            setActivityInForm(p => ({ ...p, relatedArtifact: newList }));
                          }} 
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <IconButton onClick={() => setActivityInForm(p => ({ ...p, relatedArtifact: p.relatedArtifact?.filter((_, i) => i !== index) }))} color="error"><DeleteIcon /></IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              )}
              </Grid>
            </Box>
          </Box>

          <Box sx={{ p: 3 }} display={'flex'} flexDirection={'row'} justifyContent={'flex-end'} gap={1}>
            <CancelButton disabled={saving} onClick={handleClose} />
            <LoadingButton loading={saving} onClick={handleSaveActivity} variant="contained">
              {editingActivity ? 'Save Changes' : 'Create Activity'}
            </LoadingButton>
          </Box>
        </Dialog>

        <FilterDrawer
          open={isFilterDrawerOpen}
          onCloseIconButtonClick={() => setIsFilterDrawerOpen(false)}
          onApplyButtonClick={() => setIsFilterDrawerOpen(false)}
          onClearAllButtonClick={() => {
            setStatusFilter([]);
            setKindFilter([]);
          }}
          title="Filters"
        >
          <Stack spacing={3} sx={{ p: 3 }}>
            <CheckboxList
              title="Status"
              options={activityStatusOptions}
              keyExtractor={(item) => item.code}
              labelExtractor={(item) => item.display}
              externalCheckedItems={activityStatusOptions.filter((o) => statusFilter.includes(o.code))}
              onChange={(items) => setStatusFilter(items.map((i) => i.code))}
            />
            <CheckboxList
              title="Kind"
              options={activityKindOptions}
              keyExtractor={(item) => item.code}
              labelExtractor={(item) => item.display}
              externalCheckedItems={activityKindOptions.filter((o) => kindFilter.includes(o.code))}
              onChange={(items) => setKindFilter(items.map((i) => i.code))}
            />
          </Stack>
        </FilterDrawer>
      </Container>
    </Page>
  );
}